import { BaseTextField } from '@components/BaseTextField';
import { Box, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import React, { CSSProperties, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { ellipsisStyles } from '..';
import { CameraTask } from '@EcamModel/model/CameraTask';
import { taskHttpController } from '@controllers/index';
import { pushError, pushSuccess } from '@components/toast';
import { Paging } from '@Core';
import { useBackdrop } from 'src/providers/BackdropProvider';
import usePopUp from '@hooks/usePopUp';
import PopUpWarning from '@components/PopUpWarning';
import { CellOfRow } from '@pages/overview/components/HeadOverview';
import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from '@controllers/axiosClient';
import { stickiesStyle } from '../config';

type Props = {
    taskId: number;
    taskName: string;
    taskItem?: CameraTask;
    isCheckSticky?: boolean;
    isShowTaskManagement?: boolean;
    style?: SxProps<Theme>;
};

export default function EditableTaskNameCell(props: Props) {
    const { taskId, taskName, taskItem, isCheckSticky, isShowTaskManagement, style } = props;
    const isCheckCallAPIChangeTaskName = useRef<boolean>(false);
    const queryClient = useQueryClient();
    const backdrop = useBackdrop();

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editedTaskName, setEditedTaskName] = useState<string>('');

    const popupConfirmChangeTaskName = usePopUp();

    const handleDoubleClick = () => {
        setIsEditing(true);
    };

    const handleCancelChangeTaskName = () => {
        setIsEditing(false);
        setEditedTaskName(taskName);
    };

    const handleTypingChangeTaskName = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEditedTaskName(e.target.value);
    };

    const handleBlurTextField = () => {
        if (editedTaskName && editedTaskName !== taskName && !Object.values(taskItem ?? ({} as CameraTask))?.length) {
            popupConfirmChangeTaskName.setTrue();
        } else {
            handleCancelChangeTaskName();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (editedTaskName && editedTaskName !== taskName && !isCheckCallAPIChangeTaskName.current) {
                handleUpsertTaskName(editedTaskName);
            }
        } else if (e.key === 'Escape') {
            handleCancelChangeTaskName();
        }
    };

    const upsertCameraTaskNameAPI = async (_task: CameraTask) => {
        const response = await taskHttpController.upsert(_task);
        return response;
    };

    const mutationUpsertCameraNote = useMutation(upsertCameraTaskNameAPI, {
        onSuccess: async (res) => {
            pushSuccess('Update name successfully');
            queryClient.invalidateQueries(['listTask']);

            queryClient.setQueryData<CameraTask[]>(['listTask'], (oldData) => {
                if (!oldData) return oldData!;
                return oldData.map((task) => (task.Id === res.Id ? { ...task, ...res } : task));
            });
            popupConfirmChangeTaskName.onClose();
        },
        onError: (err: AxiosError) => {
            pushError(err.message);
        },
        onSettled: () => {
            backdrop.setFalse();
            isCheckCallAPIChangeTaskName.current = false;
            setIsEditing(false);
        },
    });

    const handleUpsertTaskName = async (taskName: string) => {
        isCheckCallAPIChangeTaskName.current = true;
        backdrop.setTrue();

        const _task: CameraTask = {
            Id: taskId,
            TaskName: taskName.trim(),
        } as CameraTask;

        await mutationUpsertCameraNote.mutateAsync(_task);
    };

    useEffect(() => {
        setEditedTaskName(taskName);
    }, [taskName]);

    return (
        <CellOfRow
            value={
                <Box>
                    {isEditing ? (
                        <BaseTextField
                            autoFocus
                            size="small"
                            sx={{ width: '250px' }}
                            value={editedTaskName}
                            onKeyDown={handleKeyDown}
                            onBlur={handleBlurTextField}
                            onChange={handleTypingChangeTaskName}
                        />
                    ) : (
                        <Tooltip placement="top" title={taskName.length > 150 ? taskName : ''}>
                            <Typography
                                component="span"
                                sx={{
                                    ...ellipsisStyles,
                                    minWidth: taskName.length > 50 ? '250px' : 'max-content',
                                }}
                                onDoubleClick={handleDoubleClick}
                            >
                                {taskName}
                            </Typography>
                        </Tooltip>
                    )}
                    <PopUpWarning
                        {...popupConfirmChangeTaskName}
                        title="Confirm"
                        message={`Are you want to change the name of the task to ${editedTaskName}?`}
                        isLoading={isCheckCallAPIChangeTaskName.current}
                        onClose={() => {
                            popupConfirmChangeTaskName.onClose();
                            handleCancelChangeTaskName();
                        }}
                        onConfirm={() => {
                            if (!isCheckCallAPIChangeTaskName.current) {
                                handleUpsertTaskName(editedTaskName);
                            }
                        }}
                    />
                </Box>
            }
            sx={
                isEditing && !isCheckSticky
                    ? stickiesStyle({ style: { left: isShowTaskManagement ? '57px' : '257px' } })
                    : { ...style }
            }
            align="left"
        />
    );
}
